import React, { useState } from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import { withStyles } from "@material-ui/core/styles";
import { useSnackbar } from "notistack";
import Collapse from "@material-ui/core/Collapse";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Card from "@material-ui/core/Card";
import CardActions from "@material-ui/core/CardActions";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import WarningIcon from "@material-ui/icons/WarningRounded";
import CheckCircleOutline from "@material-ui/icons/CheckCircleOutline";
import ErrorOutline from "@material-ui/icons/ErrorOutline";
import NotificationsNone from "@material-ui/icons/NotificationsNone";

const styles = theme => ({
  card: {
    maxWidth: 400,
    minWidth: 344,
    borderRadius: 4
  },
  typography: {
    fontWeight: "bold"
  },
  actionRoot: {
    padding: "8px 8px 8px 16px",
    color: theme.palette.common.white,
    minHeight: theme.spacing(7),
    "&$error": {
      background: theme.palette.error.main
    },
    "&$notification": {
      background: theme.palette.primary.main
    },
    "&$success": {
      background: theme.palette.success.main
    },
    "&$warning": {
      backgroundColor: theme.palette.warning.main
    }
  },
  icons: {
    marginLeft: "auto !important"
  },
  expand: {
    padding: "8px 8px",
    transform: "rotate(0deg)",
    transition: theme.transitions.create("transform", {
      duration: theme.transitions.duration.shortest
    })
  },
  expandOpen: {
    transform: "rotate(180deg)"
  },
  collapse: {
    padding: 16
  },
  checkIcon: {
    fontSize: 20,
    color: "#b3b3b3",
    paddingRight: 4
  },
  button: {
    padding: 0,
    textTransform: "none"
  },
  message: {
    fontSize: theme.typography.pxToRem(14)
  },

  error: {},
  notification: {},
  success: {},
  warning: {}
});

const SnackMessage = React.forwardRef(
  ({ classes, id, title, message, options }, ref) => {
    const { closeSnackbar } = useSnackbar();
    const {
      variant = "notification",
      hideIconVariant = false,
      dismissable = true,
      collapseMessage = false
    } = { ...options };
    const [expanded, setExpanded] = useState(collapseMessage ? false : true);

    const handleDismiss = () => {
      closeSnackbar(id);
    };

    const handleExpandClick = () => {
      setExpanded(!expanded);
    };

    const icons = {
      success: CheckCircleOutline,
      notification: NotificationsNone,
      error: ErrorOutline,
      warning: WarningIcon
    };

    const Icon = icons[variant];

    return (
      <Card className={classes.card} ref={ref}>
        <CardActions
          className={classes[variant]}
          classes={{ root: classes.actionRoot }}
        >
          {!hideIconVariant && <Icon />}
          <Typography
            color="inherit"
            variant="subtitle2"
            className={classes.typography}
          >
            {title}
          </Typography>
          <div className={classes.icons}>
            {collapseMessage && (
              <IconButton
                color="inherit"
                aria-label="Show more"
                className={classnames(classes.expand, {
                  [classes.expandOpen]: expanded
                })}
                onClick={handleExpandClick}
              >
                <ExpandMoreIcon />
              </IconButton>
            )}

            {dismissable && (
              <IconButton
                color="inherit"
                className={classes.expand}
                onClick={handleDismiss}
              >
                <CloseIcon />
              </IconButton>
            )}
          </div>
        </CardActions>
        {(message || (!message && options.variant) === "error") && (
          <Collapse in={expanded} timeout="auto">
            <Paper square className={classes.collapse}>
              <Typography className={classes.message} component="div">
                {!message && options.variant === "error" ? (
                  <>Something went wrong. Please try again later.</>
                ) : (
                  <>{message}</>
                )}
              </Typography>
            </Paper>
          </Collapse>
        )}
      </Card>
    );
  }
);

SnackMessage.propTypes = {
  id: PropTypes.number.isRequired
};

export default withStyles(styles)(SnackMessage);
